import { createContext, useContext, useRef } from "react";
import { motion, useInView } from "framer-motion";

export const ThemeContext = createContext({ theme: "night", toggleTheme: () => {} });
export const useTheme = () => useContext(ThemeContext);

export const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ line, delay, lineClassName, immediate }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-10% 0px" });
    return (
        <span ref={ref} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
            <motion.span
                className={`block will-change-transform ${lineClassName}`}
                initial={{ y: "115%", opacity: 0 }}
                animate={immediate || inView ? { y: "0%", opacity: 1 } : {}}
                transition={{ duration: 1.2, ease: EASE, delay }}
            >
                {line}
            </motion.span>
        </span>
    );
};

export const MaskedLines = ({ lines, className = "", lineClassName = "", delay = 0, immediate = false }) => (
    <span className={`block ${className}`}>
        {lines.map((line, i) => (
            <MaskedLine key={i} line={line} lineClassName={lineClassName} delay={delay + i * 0.14} immediate={immediate} />
        ))}
    </span>
);

export const Reveal = ({ children, delay = 0, y = 40, className = "" }) => (
    <motion.div
        className={className}
        initial={{ opacity: 0, y }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-10% 0px" }}
        transition={{ duration: 1.1, ease: EASE, delay }}
    >
        {children}
    </motion.div>
);

export const Overline = ({ children, className = "" }) => (
    <Reveal y={16}>
        <div className={`flex items-center gap-4 ${className}`}>
            <span className="h-px w-10 bg-white/25" />
            <span className="font-mono-tech text-[11px] tracking-[0.35em] uppercase text-white/45">
                {children}
            </span>
        </div>
    </Reveal>
);

export const PrimaryButton = ({ children, onClick, testId, type = "button", disabled = false }) => (
    <button
        type={type}
        data-testid={testId}
        onClick={onClick}
        disabled={disabled}
        className="group relative overflow-hidden bg-[#F4F2EF] px-8 py-4 font-mono-tech text-[11px] tracking-[0.3em] uppercase text-[#070708] transition-colors duration-500 disabled:opacity-50"
    >
        <span className="relative z-10 transition-colors duration-500 group-hover:text-[#F4F2EF]">{children}</span>
        <span className="absolute inset-0 translate-y-full bg-[#070708] transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-y-0" />
    </button>
);

export const GhostButton = ({ children, onClick, testId }) => (
    <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        className="group relative overflow-hidden border border-white/25 px-8 py-4 font-mono-tech text-[11px] tracking-[0.3em] uppercase text-white/80 transition-colors duration-500 hover:border-white/60 hover:text-white"
    >
        <span className="relative z-10">{children}</span>
        <span className="absolute inset-0 translate-y-full bg-white/5 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-y-0" />
    </button>
);

export const scrollToId = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
};
